/**
 * The welcome letter's greeting and dateline (MYAC-1522). Block 2 of the
 * redesign uses them for the project conversation's letter title; the arrival
 * letter itself (block 6) adds when it greets, below.
 *
 * The welcome letter — what the person sees on arriving, before the app.
 *
 * Which letter they get depends only on things we know for sure: whether their
 * space has anything in it yet, when they last came, and the local time. The
 * body is the server's pre-generated opening; nothing here invents a fact.
 */
export type LetterSituation = 'first' | 'back' | 'week_end' | 'day'

/** Away this long → "Bon retour". */
export const ABSENCE_MS = 7 * 24 * 60 * 60 * 1000

export function letterSituation(opts: {
    now: Date
    lastVisit: Date | null
    firstTime: boolean
}): LetterSituation {
    const { now, lastVisit, firstTime } = opts
    if (firstTime) return 'first'
    if (lastVisit && now.getTime() - lastVisit.getTime() >= ABSENCE_MS) return 'back'
    const day = now.getDay()
    if (day === 6 || day === 0 || (day === 5 && now.getHours() >= 17)) return 'week_end'
    return 'day'
}

/** Bonsoir from 18:00 until 05:00, bonjour otherwise. */
export function isEvening(now: Date): boolean {
    const h = now.getHours()
    return h >= 18 || h < 5
}

/**
 * From 12:00 until 18:00. English says « Good afternoon » then, where « Good
 * morning » read wrong after noon (2026-10-14); French keeps « Bonjour », which
 * holds all day, through the same key.
 */
export function isAfternoon(now: Date): boolean {
    const h = now.getHours()
    return h >= 12 && h < 18
}

/**
 * The i18n key of the letter's greeting line. `named` picks the variant that
 * carries the first name — without one, the letter says "Bonjour," alone
 * rather than a placeholder or an email.
 */
export function greetingKey(situation: LetterSituation, now: Date, named: boolean): string {
    const base = situation === 'first' ? 'welcome'
        : situation === 'back' ? 'back'
            : isEvening(now) ? 'evening' : isAfternoon(now) ? 'afternoon' : 'morning'
    return `welcome_letter.greeting.${base}${named ? '_named' : ''}`
}

/** The key of the letter's second line, the one message, in the brand colour. */
export function headlineKey(situation: LetterSituation): string {
    return `welcome_letter.headline.${situation}`
}

/**
 * "Mardi 29 septembre, 9 h 12" / "Tuesday 29 September, 9:12 AM" — the letter's
 * dateline. French writes the time with an "h"; Intl does not.
 */
export function dateline(now: Date, locale: string): string {
    const lang = locale.split('-')[0].toLowerCase()
    const date = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(now)
    const time = lang === 'fr'
        ? `${now.getHours()} h ${String(now.getMinutes()).padStart(2, '0')}`
        : new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(now)
    return `${date.charAt(0).toLocaleUpperCase(locale)}${date.slice(1)}, ${time}`
}

// ── When the letter greets (block 6) ───────────────────────────────────────
// At a fresh sign-in, and at the first visit of the day (Aymeric 08/10). A reload
// or a new tab later the same day goes straight to the app: the letter is a
// welcome, and seen at every reload it would be a gate. Only an arrival on the
// dashboard opens it; a deep link keeps its page.

/** The routes a person comes from when they have just signed in. */
const SIGN_IN_ROUTES = new Set(['login', 'register'])
/** …and the ones that still count as arriving (nothing of the app seen yet). */
const ARRIVAL_ROUTES = new Set([...SIGN_IN_ROUTES, 'verify-email', 'choose-tenant'])

/**
 * Whether this navigation is an arrival: the first one of the page load (no
 * route before it), or straight after signing in or choosing a company.
 */
export function isArrival(from: { name?: unknown, matched: readonly unknown[] }): boolean {
    return from.matched.length === 0 || ARRIVAL_ROUTES.has(String(from.name ?? ''))
}

export function isFreshSignIn(from: { name?: unknown }): boolean {
    return SIGN_IN_ROUTES.has(String(from.name ?? ''))
}

const LAST_SHOWN_PREFIX = 'mac.welcomeLetter.lastShown.'
const HIDDEN_PREFIX = 'mac.welcomeLetter.hidden.'

/** The local calendar day, as the person lives it. */
export function dayKey(now: Date): string {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function read(key: string): string | null {
    try { return localStorage.getItem(key) } catch { return null }
}

function write(key: string, value: string | null): void {
    try {
        if (value === null) localStorage.removeItem(key)
        else localStorage.setItem(key, value)
    } catch { /* storage refused: this load only */ }
}

/**
 * The person's « Ne plus afficher » choice lives on their user doc; this browser
 * keeps a copy because the decision is taken before the account has loaded.
 */
export function readWelcomeLetterHiddenHere(uid: string): boolean {
    return read(HIDDEN_PREFIX + uid) === '1'
}

export function writeWelcomeLetterHiddenHere(uid: string, hidden: boolean): void {
    write(HIDDEN_PREFIX + uid, hidden ? '1' : null)
}

export function shouldShowWelcomeLetter(uid: string, opts: { freshSignIn: boolean, now?: Date }): boolean {
    if (!uid || readWelcomeLetterHiddenHere(uid)) return false
    return opts.freshSignIn || read(LAST_SHOWN_PREFIX + uid) !== dayKey(opts.now ?? new Date())
}

export function markWelcomeLetterShown(uid: string, now: Date = new Date()): void {
    if (uid) write(LAST_SHOWN_PREFIX + uid, dayKey(now))
}


/**
 * A generated letter's text in the reader's language, else the other one when it is
 * empty: the message and its replies alike, so a letter never loses its replies to
 * an empty translation (MYAC-1522, B3). The letter page and the dashboard read it so.
 */
export function inLetterLanguage(text: Partial<Record<string, string>> | null | undefined, locale: string): string {
    if (!text) return ''
    const lang = locale.split('-')[0].toLowerCase() === 'fr' ? 'fr' : 'en'
    const other = lang === 'fr' ? 'en' : 'fr'
    return text[lang]?.trim() || text[other]?.trim() || ''
}

/** The letter's paragraphs, in the reader's language (blank lines part them). */
export function letterParagraphs(text: Partial<Record<string, string>> | null | undefined, locale: string): string[] {
    return inLetterLanguage(text, locale).split(/\n{2,}/).map(p => p.trim()).filter(Boolean)
}
